import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { SearchUserInputComponent } from 'components/search-user-input/search-user-input.component';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import type { BackofficeInformationRequestCard, InformationRequestTaskCard } from 'types/information-request.interface';
import type { ClassifierValueCard } from 'types/classifier.interface';
import type { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { getControlsWithErrors } from 'util/form.utility';
import { BaseFormModalComponent } from '../base-form-modal.component';

type AssigneeType = 'user' | 'structuralUnit';

const MAX_TEXT_LENGTH = 255;
const TASK_TYPE_CLASSIFIER_CODE = 'InformationRequestTaskType';

@Component({
  selector: 'app-ir-create-task-modal',
  standalone: true,
  imports: [
    TranslateModule,
    SpinnerComponent,
    NgClass,
    TooltipComponent,
    ReactiveFormsModule,
    SearchUserInputComponent,
    DatePickerComponent
  ],
  templateUrl: './ir-create-task-modal.component.html',
  styleUrls: ['./ir-create-task-modal.component.scss', '../../form/base-form.scss']
})
export class IrCreateTaskModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  readonly maxTextLength = MAX_TEXT_LENGTH;
  readonly DateRangeMode = DateRangeMode;

  @Input() isOpen!: boolean;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() minExecutionOrder: number = 1;
  @Input() isFirstTask: boolean = false;
  @Output() taskCreated: EventEmitter<InformationRequestTaskCard> = new EventEmitter<InformationRequestTaskCard>();
  @ViewChild('content') content!: TemplateRef<any>;

  isCrudLoading = false;
  shouldRenderTooltip = false;
  assigneeType: AssigneeType = 'user';
  taskTypes: ClassifierValueCard[] = [];
  structuralUnits: StructuralUnitMinimalListItem[] = [];
  executionOrderOptions: number[] = [];

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.loadData();
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.loadData();
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  private loadData(): void {
    this.apiService.getClassifierValues(TASK_TYPE_CLASSIFIER_CODE).subscribe({
      next: (card) => {
        this.taskTypes = (card.values || []).filter(v => !v.isDeleted);
      },
      error: (err) => {
        console.error('Failed to load task type classifier:', err);
      }
    });

    this.apiService.getStructuralUnitOptions().subscribe((units) => {
      this.structuralUnits = units;
    });
  }

  fillForm(): void {
    this.assigneeType = 'user';
    const startOrder = this.isFirstTask ? 1 : this.minExecutionOrder;
    const maxOrder = Math.max(startOrder + 9, 10);
    this.executionOrderOptions = this.isFirstTask
      ? [1]
      : Array.from({ length: maxOrder - startOrder + 1 }, (_, i) => startOrder + i);
    this.form = this.formBuilder.group({
      assigneeUserId: [null, [Validators.required]],
      assigneeStructuralUnitId: [null],
      taskTypeClassifierValueId: ['', [Validators.required]],
      text: ['', [Validators.maxLength(MAX_TEXT_LENGTH)]],
      executionOrder: [startOrder, [Validators.required, Validators.min(1)]],
      executionDate: ['', [Validators.required]]
    });
  }

  onAssigneeTypeChange(type: AssigneeType): void {
    this.assigneeType = type;
    if (type === 'user') {
      this.form.get('assigneeUserId')!.setValidators([Validators.required]);
      this.form.get('assigneeStructuralUnitId')!.clearValidators();
      this.form.get('assigneeStructuralUnitId')!.setValue(null);
    } else {
      this.form.get('assigneeStructuralUnitId')!.setValidators([Validators.required]);
      this.form.get('assigneeUserId')!.clearValidators();
      this.form.get('assigneeUserId')!.setValue(null);
    }
    this.form.get('assigneeUserId')!.updateValueAndValidity();
    this.form.get('assigneeStructuralUnitId')!.updateValueAndValidity();
  }

  onUserSelected(userId: string): void {
    this.form.get('assigneeUserId')!.setValue(userId);
  }

  onDateSelected(date: string): void {
    this.form.get('executionDate')!.setValue(date);
  }

  protected override open(content: any): void {
    this.modalService.open(content, { backdrop: 'static', keyboard: false, windowClass: 'ir-create-task-modal' });
  }

  protected override close(): void {
    super.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      const formValue = this.form.value;

      const args = {
        informationRequestId: this.informationRequest.id,
        assigneeUserId: this.assigneeType === 'user' ? formValue.assigneeUserId : null,
        assigneeStructuralUnitId: this.assigneeType === 'structuralUnit' ? formValue.assigneeStructuralUnitId : null,
        taskTypeClassifierValueId: formValue.taskTypeClassifierValueId,
        text: formValue.text || null,
        executionOrder: formValue.executionOrder,
        executionDate: formValue.executionDate
      };

      this.apiService.createIrTask(args).subscribe({
        next: (response) => {
          this.toastService.showSuccess('createIrTaskSuccess', '', { shouldTranslate: true });
          this.isCrudLoading = false;
          this.close();
          this.taskCreated.emit(response);
        },
        error: () => {
          this.toastService.showRequestError();
          this.isCrudLoading = false;
        }
      });
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.createIrTask');
    }
  }
}
